import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Sparkles, ArrowRight, Copy, CheckCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AIErrorHelperProps {
  databaseId: string;
  query: string;
  error: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyFix: (fixedQuery: string) => void;
}

export function AIErrorHelper({
  databaseId,
  query,
  error,
  open,
  onOpenChange,
  onApplyFix,
}: AIErrorHelperProps) {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState<{
    explanation: string;
    fix: string;
    preventionTips: string[];
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchExplanation = async () => {
    setLoading(true);
    try {
      const result = await api.explainError(databaseId, query, error);
      setExplanation(result);
    } catch (err: any) {
      toast.error('Failed to get AI assistance');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !explanation && !loading) {
      fetchExplanation();
    }
  }, [open]);

  const handleCopyFix = async () => {
    if (explanation?.fix) {
      await navigator.clipboard.writeText(explanation.fix);
      setCopied(true);
      toast.success('Fixed query copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApplyFix = () => {
    if (explanation?.fix) {
      onApplyFix(explanation.fix);
      onOpenChange(false);
      toast.success('Fixed query applied - ready to execute');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Query Error Detected
          </DialogTitle>
          <DialogDescription>
            AI is analyzing your query and suggesting a fix
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                <p className="text-sm text-muted-foreground">AI is analyzing the error...</p>
              </div>
            </div>
          ) : explanation ? (
            <div className="space-y-6">
              <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-red-700 dark:text-red-400">
                    Original Error
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-mono text-red-600 dark:text-red-300">
                    {error}
                  </p>
                </CardContent>
              </Card>

              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI Explanation
                </h4>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm leading-relaxed">{explanation.explanation}</p>
                  </CardContent>
                </Card>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Suggested Fix
                </h4>
                <Card className="border-green-200 dark:border-green-900">
                  <CardContent className="pt-4">
                    <div className="rounded-md bg-muted p-3 mb-3">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                        {explanation.fix}
                      </pre>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopyFix}
                        className="gap-2"
                      >
                        {copied ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleApplyFix}
                        className="gap-2"
                      >
                        <ArrowRight className="h-4 w-4" />
                        Apply Fix
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {explanation.preventionTips.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Prevention Tips</h4>
                    <Card>
                      <CardContent className="pt-4">
                        <ul className="space-y-2">
                          {explanation.preventionTips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                                {i + 1}
                              </span>
                              <span className="text-sm leading-relaxed">{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
